/**
 * SharedAssetsLibraryPage.jsx — iter-299m (Feb 2026)
 *
 * Admin page to manage the shared image library for all platforms.
 * Path: /control/shared-assets
 *
 * Features:
 *  - Upload image (PNG/SVG/JPG/WebP) with Arabic + English name + category + tags
 *  - Grid of asset cards with thumbnail + stable asset_id + copy-to-clipboard
 *  - Filter by category + full-text search
 *  - Replace image for an existing asset (keeps same ID, propagates to all platforms)
 *  - Delete asset
 *  - View live preview and dimensions
 *
 * Every platform (merchant, customer) uses the stable asset_id by calling:
 *   GET /api/platform-assets/{asset_id}/image  (X-AI-Core-Key + X-Caller-Platform)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Trash2, RefreshCw, Copy, Check, Download,
  Image as ImageIcon, Search, Plus, X, Edit3, Loader2, Languages,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  all:                 { ar: 'الكل',               en: 'All' },
  platform_logos:      { ar: 'شعارات المنصات',      en: 'Platform logos' },
  partner_logos:       { ar: 'شعارات شركاء/خدمات',  en: 'Partner logos' },
  telecom_logos:       { ar: 'شعارات شركات اتصالات', en: 'Telecom logos' },
  bank_logos:          { ar: 'شعارات بنوك',          en: 'Bank logos' },
  payment_methods:     { ar: 'طرق الدفع',            en: 'Payment methods' },
  country_flags:       { ar: 'أعلام الدول',          en: 'Country flags' },
  currency_icons:      { ar: 'أيقونات العملات',      en: 'Currency icons' },
  government_logos:    { ar: 'شعارات حكومية',        en: 'Government logos' },
  document_templates:  { ar: 'قوالب مستندات',        en: 'Document templates' },
  city_landmarks:      { ar: 'معالم المدن',          en: 'City landmarks' },
  general:             { ar: 'عام',                  en: 'General' },
};

function categoryLabel(cat, lang) {
  const base = CATEGORY_LABELS[cat];
  if (base) return base[lang] || base.ar;
  return cat;
}

/* ────────────── Upload / Edit Dialog ────────────── */
function AssetFormDialog({ open, onClose, onSaved, asset = null, lang = 'ar', categories = [] }) {
  const t = (ar, en) => (lang === 'ar' ? ar : en);
  const [file, setFile]     = useState(null);
  const [preview, setPreview] = useState(null);
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [cat, setCat]       = useState('general');
  const [tags, setTags]     = useState('');
  const [aid, setAid]       = useState('');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState({ ar2en: false, en2ar: false });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setNameAr(asset.name_ar || '');
      setNameEn(asset.name_en || '');
      setCat(asset.category || 'general');
      setTags((asset.tags || []).join(', '));
      setAid(asset.id);
      setFile(null);
      setPreview(null);
      axios.get(`${API_URL}/api/admin/shared-assets/${asset.id}/image`, { headers: getAuthHeaders(), responseType: 'blob' })
        .then((r) => setPreview(URL.createObjectURL(r.data))).catch(() => setPreview(null));
    } else {
      setNameAr(''); setNameEn(''); setCat('general'); setTags(''); setAid('');
      setFile(null); setPreview(null);
    }
  }, [open, asset]);

  if (!open) return null;

  const isEdit = !!asset;

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error(t('يُسمح بالصور فقط', 'Images only'));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t('الحجم أكبر من 5 ميجا', 'File exceeds 5 MB'));
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const translate = async (direction) => {
    const src = direction === 'ar2en' ? nameAr.trim() : nameEn.trim();
    if (!src) {
      toast.error(t('اكتب الاسم أولاً', 'Enter the name first'));
      return;
    }
    setTranslating((s) => ({ ...s, [direction]: true }));
    try {
      const r = await axios.post(
        `${API_URL}/api/utils/translate`,
        {
          text:   src,
          source: direction === 'ar2en' ? 'ar' : 'en',
          target: direction === 'ar2en' ? 'en' : 'ar',
        },
        { headers: getAuthHeaders() },
      );
      const out = (r.data?.translated || '').trim();
      if (!out) throw new Error('empty');
      if (direction === 'ar2en') setNameEn(out); else setNameAr(out);
      toast.success(t('تمت الترجمة', 'Translated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشلت الترجمة', 'Translation failed'));
    } finally {
      setTranslating((s) => ({ ...s, [direction]: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file) {
      toast.error(t('اختر صورة أولاً', 'Pick an image first'));
      return;
    }
    if (!nameAr.trim()) {
      toast.error(t('الاسم بالعربية مطلوب', 'Arabic name is required'));
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        // Update metadata
        await axios.put(
          `${API_URL}/api/admin/shared-assets/${asset.id}`,
          {
            name_ar:  nameAr.trim(),
            name_en:  nameEn.trim(),
            category: cat,
            tags:     tags.split(',').map(s => s.trim()).filter(Boolean),
          },
          { headers: getAuthHeaders() },
        );
        // If a new file was picked, replace the image too
        if (file) {
          const fd = new FormData();
          fd.append('image', file);
          await axios.patch(
            `${API_URL}/api/admin/shared-assets/${asset.id}/image`,
            fd,
            { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } },
          );
        }
        toast.success(t('تم الحفظ', 'Saved'));
      } else {
        const fd = new FormData();
        fd.append('image', file);
        fd.append('name_ar',  nameAr.trim());
        fd.append('name_en',  nameEn.trim());
        fd.append('category', cat);
        fd.append('tags',     tags);
        if (aid.trim()) fd.append('asset_id', aid.trim());
        const res = await axios.post(
          `${API_URL}/api/admin/shared-assets`,
          fd,
          { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } },
        );
        toast.success(t(`تم الرفع — id: ${res.data.data.id}`, `Uploaded — id: ${res.data.data.id}`));
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      data-testid="asset-form-dialog"
    >
      <div className="w-full max-w-2xl bg-[#0c0c0e] border border-sky-500/30 shadow-[0_0_60px_rgba(56,189,248,0.15)] rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-sky-400" />
            {isEdit ? t('تعديل الصورة', 'Edit asset') : t('رفع صورة جديدة', 'Upload new asset')}
          </h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Dropzone / Preview */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="relative aspect-video rounded-xl border-2 border-dashed border-white/15 bg-[#050505] flex items-center justify-center cursor-pointer hover:border-sky-500/50 transition"
            data-testid="asset-dropzone"
          >
            {preview ? (
              <>
                <img src={preview} alt="preview" className="max-w-full max-h-full object-contain p-4" />
                <div className="absolute bottom-2 end-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded">
                  {t('اضغط لتغيير الصورة', 'Click to change')}
                </div>
              </>
            ) : (
              <div className="text-center text-white/50">
                <Upload className="w-10 h-10 mx-auto mb-2 opacity-70" />
                <p className="text-sm">{t('اسحب الصورة هنا أو اضغط للاختيار', 'Drag an image here or click to pick')}</p>
                <p className="text-[10px] mt-1 opacity-60">PNG · SVG · JPG · WebP · حد أقصى 5MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="asset-file-input"
            />
          </div>

          {/* Names + translate bot */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/60 block">
                  {t('الاسم بالعربية ★', 'Arabic name ★')}
                </label>
                <button
                  type="button"
                  onClick={() => translate('en2ar')}
                  disabled={translating.en2ar || !nameEn.trim()}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/20 disabled:opacity-40"
                  data-testid="asset-translate-en2ar"
                  title={t('ترجم من الإنجليزية إلى العربية', 'Translate EN → AR')}
                >
                  {translating.en2ar
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <><Languages className="w-3 h-3" /> EN → AR</>}
                </button>
              </div>
              <input
                value={nameAr}
                onChange={(e) => setNameAr(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-10 px-3 text-white text-sm focus:border-sky-500/50 focus:outline-none"
                data-testid="asset-name-ar"
                placeholder={t('شعار اتصالات', 'شعار اتصالات')}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/60 block">
                  {t('الاسم بالإنجليزية', 'English name')}
                </label>
                <button
                  type="button"
                  onClick={() => translate('ar2en')}
                  disabled={translating.ar2en || !nameAr.trim()}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-200 hover:bg-blue-500/20 disabled:opacity-40"
                  data-testid="asset-translate-ar2en"
                  title={t('ترجم من العربية إلى الإنجليزية', 'Translate AR → EN')}
                >
                  {translating.ar2en
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <><Languages className="w-3 h-3" /> AR → EN</>}
                </button>
              </div>
              <input
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                dir="ltr"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-10 px-3 text-white text-sm focus:border-sky-500/50 focus:outline-none"
                data-testid="asset-name-en"
                placeholder="Etisalat Logo"
              />
            </div>
          </div>

          {/* Category + Tags */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-white/60 mb-1 block">{t('التصنيف', 'Category')}</label>
              <select
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-10 px-3 text-white text-sm [color-scheme:dark]"
                data-testid="asset-category"
              >
                {Object.entries(CATEGORY_LABELS)
                  .filter(([k]) => k !== 'all')
                  .map(([k, v]) => (
                    <option key={k} value={k} className="bg-[#0a0a0a]">
                      {v[lang] || v.ar}
                    </option>
                  ))}
                {categories.filter(c => !(c in CATEGORY_LABELS) && c !== 'all').map(c => (
                  <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-white/60 mb-1 block">
                {t('الوسوم (مفصولة بفاصلة)', 'Tags (comma-separated)')}
              </label>
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                dir="ltr"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-10 px-3 text-white text-sm"
                placeholder="uae, telecom, logo"
                data-testid="asset-tags"
              />
            </div>
          </div>

          {/* Custom asset_id (only on create) */}
          {!isEdit && (
            <div>
              <label className="text-[11px] text-white/60 mb-1 block">
                {t('المعرّف الثابت (اختياري — سيُولَّد تلقائياً)', 'Stable ID (optional — auto-generated)')}
              </label>
              <input
                value={aid}
                onChange={(e) => setAid(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                dir="ltr"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-10 px-3 text-white text-sm font-mono"
                placeholder="ast-etisalat-logo"
                data-testid="asset-id"
              />
              <p className="text-[10px] text-white/40 mt-1">
                {t('هذا هو الكود الثابت الذي ترسله للمنصّات — تقرأ به الصورة خادماً-لخادم بمفتاحها، ولا يوجد رابط عام. لا يمكن تغييره بعد الإنشاء.',
                   'This is the stable code you share with platforms — they read it server-to-server with their key; no public link exists. Cannot be changed after creation.')}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-white/5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-xl bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10"
            >
              {t('إلغاء', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 h-10 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="asset-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? t('حفظ التغييرات', 'Save changes') : t('رفع', 'Upload')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}


/* Fetches a JWT-protected asset image and returns an object URL (no bare public link exists). */
const _blobCache = new Map();
function useAssetBlob(assetId, version) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!assetId) { setSrc(null); return undefined; }
    const key = `${assetId}:${version || ''}`;
    if (_blobCache.has(key)) { setSrc(_blobCache.get(key)); return undefined; }
    let alive = true;
    axios.get(`${API_URL}/api/admin/shared-assets/${assetId}/image`, { headers: getAuthHeaders(), responseType: 'blob' })
      .then((r) => { const u = URL.createObjectURL(r.data); _blobCache.set(key, u); if (alive) setSrc(u); })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; };
  }, [assetId, version]);
  return src;
}

/* ────────────── Asset Card ────────────── */
function AssetCard({ asset, lang, onEdit, onDelete, onCopy }) {
  const t = (ar, en) => (lang === 'ar' ? ar : en);
  const [copied, setCopied] = useState(false);
  const thumb = useAssetBlob(asset.id, asset.updated_at);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asset.id);
      setCopied(true);
      onCopy && onCopy(asset.id);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('فشل النسخ', 'Copy failed'));
    }
  };
  return (
    <div
      className="group bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-sky-500/40 transition flex flex-col"
      data-testid={`asset-card-${asset.id}`}
    >
      {/* Preview */}
      <div className="aspect-square bg-[#050505] border-b border-white/5 flex items-center justify-center p-3 relative">
        {thumb ? (
          <img src={thumb} alt={asset.name_ar} className="max-w-full max-h-full object-contain" />
        ) : (
          <ImageIcon className="w-8 h-8 text-white/15" />
        )}
        {asset.width && asset.height && (
          <div className="absolute top-2 start-2 bg-black/60 text-white/70 text-[9px] px-1.5 py-0.5 rounded font-mono">
            {asset.width}×{asset.height}
          </div>
        )}
        <div className="absolute top-2 end-2 bg-black/60 text-white/70 text-[9px] px-1.5 py-0.5 rounded font-mono uppercase">
          {(asset.mime || '').replace('image/', '')}
        </div>
      </div>

      {/* Meta */}
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="min-h-[2.5rem]">
          <h4 className="text-white text-sm font-bold truncate" title={asset.name_ar}>
            {asset.name_ar}
          </h4>
          {asset.name_en && (
            <p className="text-white/50 text-[11px] truncate" dir="ltr" title={asset.name_en}>
              {asset.name_en}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/30">
            {categoryLabel(asset.category, lang)}
          </span>
          {(asset.tags || []).slice(0, 2).map(tt => (
            <span key={tt} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/50">#{tt}</span>
          ))}
        </div>

        {/* Asset CODE — the only thing shared with platforms (no public link exists) */}
        <button
          onClick={copy}
          title={t('نسخ كود الصورة — أرسله للمنصّات (لا يوجد رابط عام)', 'Copy asset code — share with platforms (no public link)')}
          className="flex items-center gap-1.5 bg-[#d4af37]/10 border border-[#d4af37]/40 rounded-lg px-2 py-1.5 w-full hover:bg-[#d4af37]/20 transition group/copy"
          data-testid={`asset-copy-${asset.id}`}
        >
          <span className="text-[9px] text-[#d4af37]/70 shrink-0">{t('كود', 'code')}</span>
          <span className="font-mono text-[11px] text-[#d4af37] truncate flex-1 text-start" dir="ltr">
            {asset.id}
          </span>
          {copied
            ? <Check className="w-3 h-3 text-emerald-400 shrink-0" />
            : <Copy className="w-3 h-3 text-[#d4af37]/70 shrink-0 group-hover/copy:text-[#d4af37]" />}
        </button>

        {/* Actions */}
        <div className="flex items-center gap-1 pt-1">
          <button
            onClick={() => onEdit(asset)}
            className="flex-1 h-8 rounded-lg bg-white/5 border border-white/10 text-white/80 text-xs hover:bg-white/10 flex items-center justify-center gap-1"
            data-testid={`asset-edit-${asset.id}`}
          >
            <Edit3 className="w-3 h-3" />
            {t('تعديل', 'Edit')}
          </button>
          <button
            onClick={() => onDelete(asset)}
            className="h-8 w-8 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 flex items-center justify-center"
            data-testid={`asset-delete-${asset.id}`}
            title={t('حذف نهائي', 'Delete permanently')}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────── Main Page ────────────── */
export default function SharedAssetsLibraryPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [assets, setAssets]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [importing, setImporting] = useState(false);

  const importDefaults = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/shared-assets/import-defaults`, null, { headers: getAuthHeaders() });
      const { imported = [], skipped = [], failed = [] } = r.data || {};
      toast.success(t(`تم استيراد ${imported.length} · موجود مسبقاً ${skipped.length} · فشل ${failed.length}`,
                      `Imported ${imported.length} · already present ${skipped.length} · failed ${failed.length}`));
      fetchAssets();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الاستيراد', 'Import failed'));
    } finally {
      setImporting(false);
    }
  };

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('category', filter);
      if (search.trim())    params.set('search', search.trim());
      params.set('limit', '500');
      const res = await axios.get(
        `${API_URL}/api/admin/shared-assets?${params.toString()}`,
        { headers: getAuthHeaders() },
      );
      setAssets(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التحميل' : 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [filter, search, isAr]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/shared-assets/categories`,
        { headers: getAuthHeaders() },
      );
      setCategories(['all', ...Object.keys(res.data.counts)]);
      setCategoryCounts(res.data.counts || {});
    } catch (err) {
      console.log('categories error:', err.message);
    }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const totalAssets = useMemo(() => Object.values(categoryCounts).reduce((a, b) => a + b, 0), [categoryCounts]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/shared-assets/${deleteTarget.id}`,
        { headers: getAuthHeaders() },
      );
      toast.success(t(`تم الحذف — ${deleteTarget.id}`, `Deleted — ${deleteTarget.id}`));
      setDeleteTarget(null);
      fetchAssets();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  const breadcrumb = [
    { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
    { labelAr: 'قاعدة صور المنصات', labelEn: 'Shared Images Library', path: null },
  ];

  return (
    <DashboardLayout
      title="قاعدة صور المنصات"
      titleEn="Shared Images Library"
      onBack={() => navigate('/dashboard?view=platform-control')}
      breadcrumb={breadcrumb}
    >
      <div className="max-w-7xl mx-auto p-4 sm:p-6" dir={isAr ? 'rtl' : 'ltr'} data-testid="shared-assets-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center shrink-0">
              <ImageIcon className="w-6 h-6 text-sky-400" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                {t('قاعدة صور المنصات', 'Shared Platform Images Library')}
                <span className="text-sm font-normal text-white/50">({totalAssets})</span>
              </h1>
              <p className="text-white/50 text-xs mt-1">
                {t('ارفع الصورة مرة واحدة — كل المنصّات تقرأها بالكود (asset_id) خادماً-لخادم، بلا أي رابط عام',
                   'Upload once — every platform pulls it by stable asset_id')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={importDefaults}
              disabled={importing}
              className="h-10 px-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm hover:bg-emerald-500/20 flex items-center gap-2 disabled:opacity-50"
              data-testid="import-defaults-btn"
              title={t('استيراد شعارات المنصة الحالية إلى المكتبة', 'Import the platform\'s existing logos into the library')}
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {t('استيراد صور المنصة', 'Import platform images')}
            </button>
            <button
              onClick={() => { fetchAssets(); fetchCategories(); }}
              disabled={loading}
              className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {t('تحديث', 'Refresh')}
            </button>
            <button
              onClick={() => setUploadOpen(true)}
              className="h-10 px-4 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold flex items-center gap-2"
              data-testid="upload-asset-btn"
            >
              <Plus className="w-4 h-4" />
              {t('رفع صورة', 'Upload')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute inset-y-0 my-auto start-3 w-4 h-4 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('بحث بالاسم، المعرّف، أو الوسوم', 'Search by name, id, or tag')}
              className="w-full ps-10 pe-3 h-10 bg-[#050505] border border-white/10 rounded-xl text-white text-sm focus:border-sky-500/50 focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#0a0a0a] p-1 rounded-xl border border-white/10 overflow-x-auto max-w-full">
            {categories.map((c) => {
              const count = c === 'all' ? totalAssets : (categoryCounts[c] || 0);
              const active = filter === c;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                    active ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40' : 'text-white/50 hover:text-white border border-transparent'
                  }`}
                  data-testid={`filter-${c}`}
                >
                  {categoryLabel(c, language)}
                  {count > 0 && <span className="ms-1 opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-20 text-center text-white/50">
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-3" />
            {t('جارٍ التحميل...', 'Loading...')}
          </div>
        ) : assets.length === 0 ? (
          <div className="py-20 text-center bg-white/[0.02] rounded-2xl border border-white/10">
            <ImageIcon className="w-16 h-16 mx-auto mb-4 text-white/30" />
            <p className="text-white font-bold text-lg">
              {t('لا توجد صور بعد', 'No assets yet')}
            </p>
            <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">
              {t('ابدأ برفع شعارات الشركات (اتصالات، دو، البنوك) ليستخدمها كل المنصّات.',
                 'Start by uploading company logos (telecom, banks) so every platform can share them.')}
            </p>
            <button
              onClick={() => setUploadOpen(true)}
              className="mt-5 h-10 px-5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('رفع أول صورة', 'Upload first asset')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {assets.map((a) => (
              <AssetCard
                key={a.id}
                asset={a}
                lang={language}
                onEdit={setEditTarget}
                onDelete={setDeleteTarget}
                onCopy={(id) => toast.success(t(`تم نسخ الكود ${id}`, `Copied code ${id}`))}
              />
            ))}
          </div>
        )}

        {/* Upload / Edit dialog */}
        <AssetFormDialog
          open={uploadOpen || !!editTarget}
          asset={editTarget}
          onClose={() => { setUploadOpen(false); setEditTarget(null); }}
          onSaved={() => { fetchAssets(); fetchCategories(); }}
          lang={language}
          categories={categories}
        />

        {/* Delete confirmation */}
        {deleteTarget && createPortal(
          <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !deleting) setDeleteTarget(null); }}
            data-testid="asset-delete-dialog"
          >
            <div className="w-full max-w-md bg-[#0c0c0e] border border-red-500/40 shadow-[0_0_60px_rgba(239,68,68,0.15)] rounded-2xl p-6" dir={isAr ? 'rtl' : 'ltr'}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                {t('حذف نهائي', 'Delete permanently')}
              </h3>
              <p className="text-white/70 text-sm mt-3">
                {isAr ? (
                  <>سيُحذف <span className="font-mono text-red-300">{deleteTarget.id}</span> من قاعدة البيانات. أي منصّة ترجع له ستحصل على 404.</>
                ) : (
                  <>Asset <span className="font-mono text-red-300">{deleteTarget.id}</span> will be removed. Any platform referencing it will get 404.</>
                )}
              </p>
              <p className="text-red-300 text-xs mt-2 font-semibold">
                {t('⚠️ هذا الإجراء لا يمكن التراجع عنه.', '⚠️ This action cannot be undone.')}
              </p>
              <div className="flex items-center gap-2 mt-5">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 h-10 rounded-xl bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10"
                >
                  {t('إلغاء', 'Cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 h-10 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
                  data-testid="confirm-delete-btn"
                >
                  {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('حذف نهائي', 'Delete')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </DashboardLayout>
  );
}
